import XLSX from 'xlsx-js-style/dist/xlsx.min.js'

const HIDE_SIZE_CATEGORIES = new Set([13, 19, 29, 30, 31, 40, 43, 45])
const SHOW_AREAS_CATEGORIES = new Set([20, 25, 26, 27, 28, 32, 33, 44])

/** 与 offerExcel.css 保持一致的主题色 */
export const EXCEL_THEME = {
  titleColor: '0E0E0E',
  titleBorder: '217346',
  headerBg: 'CCCCCC',
  headerColor: '222222',
  bodyColor: '333333',
  infoColor: '444444',
  labelDark: '222222',
  zebraBg: 'F9FBF9',
  footerBg: 'E8E8E8',
  borderColor: '000000',
  white: 'FFFFFF',
}

/** Excel 字体：标题黑体，正文宋体 */
const FONT_TITLE = 'SimHei'
const FONT_BODY = 'SimSun'

function showSizeColumn(category) {
  return !HIDE_SIZE_CATEGORIES.has(Number(category))
}

function showAreasColumn(category) {
  return SHOW_AREAS_CATEGORIES.has(Number(category))
}

function borderThin(color = EXCEL_THEME.borderColor) {
  const edge = { style: 'thin', color: { rgb: color } }
  return { top: edge, bottom: edge, left: edge, right: edge }
}

function baseStyle(extra = {}) {
  return Object.assign({
    font: { name: FONT_BODY, sz: 11, color: { rgb: EXCEL_THEME.bodyColor } },
    alignment: { vertical: 'center', wrapText: true },
    border: borderThin(),
  }, extra)
}

function titleStyle() {
  return baseStyle({
    font: { name: FONT_TITLE, sz: 22, bold: true, color: { rgb: EXCEL_THEME.titleColor } },
    fill: { fgColor: { rgb: EXCEL_THEME.white } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: EXCEL_THEME.borderColor } },
      left: { style: 'thin', color: { rgb: EXCEL_THEME.borderColor } },
      right: { style: 'thin', color: { rgb: EXCEL_THEME.borderColor } },
      bottom: { style: 'medium', color: { rgb: EXCEL_THEME.titleBorder } },
    },
  })
}

function infoLineStyle() {
  return baseStyle({
    font: { name: FONT_BODY, sz: 14, bold: true, color: { rgb: EXCEL_THEME.infoColor } },
    fill: { fgColor: { rgb: EXCEL_THEME.white } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
  })
}

function subtitleStyle() {
  return baseStyle({
    font: { name: FONT_BODY, sz: 16, bold: true, color: { rgb: EXCEL_THEME.bodyColor } },
    fill: { fgColor: { rgb: EXCEL_THEME.white } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: EXCEL_THEME.borderColor } },
      left: { style: 'thin', color: { rgb: EXCEL_THEME.borderColor } },
      right: { style: 'thin', color: { rgb: EXCEL_THEME.borderColor } },
      bottom: { style: 'none' },
    },
  })
}

function headerStyle() {
  return baseStyle({
    font: { name: FONT_BODY, sz: 12, bold: true, color: { rgb: EXCEL_THEME.headerColor } },
    fill: { fgColor: { rgb: EXCEL_THEME.headerBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
  })
}

function cellStyle(zebra = false) {
  return baseStyle({
    font: { name: FONT_BODY, sz: 12, bold: false, color: { rgb: EXCEL_THEME.bodyColor } },
    fill: { fgColor: { rgb: zebra ? EXCEL_THEME.zebraBg : EXCEL_THEME.white } },
    alignment: { horizontal: 'center', vertical: 'center' },
  })
}

function footerStyle() {
  return baseStyle({
    font: { name: FONT_BODY, sz: 14, bold: true, color: { rgb: EXCEL_THEME.bodyColor } },
    fill: { fgColor: { rgb: EXCEL_THEME.footerBg } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
  })
}

function moneyText(value) {
  if (value === '' || value === null || value === undefined) return '￥ 0'
  return `￥ ${value}`
}

function cellRef(r, c) {
  return XLSX.utils.encode_cell({ r, c })
}

function setCell(ws, r, c, value, style) {
  const ref = cellRef(r, c)
  if (value === '' || value === null || value === undefined) {
    ws[ref] = { t: 's', v: '', s: style }
    return
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    ws[ref] = { t: 'n', v: value, s: style }
    return
  }
  ws[ref] = { t: 's', v: String(value), s: style }
}

function pushMerge(merges, r1, c1, r2, c2) {
  if (r1 === r2 && c1 === c2) return
  merges.push({ s: { r: r1, c: c1 }, e: { r: r2, c: c2 } })
}

function buildHeaders(category) {
  const showSize = showSizeColumn(category)
  const showAreas = showAreasColumn(category)
  const headers = ['序号', '名称']
  if (showSize) headers.push('尺寸(mm)')
  headers.push('规格参数')
  if (showAreas) headers.push('面积(㎡)')
  headers.push('数量', '单位', '单价', '金额', '备注')
  return { headers, showSize, showAreas }
}

function buildColumnWidths(headers) {
  const widthMap = {
    '序号': 6,
    '名称': 24,
    '尺寸(mm)': 16,
    '规格参数': 20,
    '面积(㎡)': 10,
    '数量': 8,
    '单位': 8,
    '单价': 12,
    '金额': 12,
    '备注': 22,
  }
  return headers.map((header) => ({ wch: widthMap[header] || 12 }))
}

import { cartDisplayName } from '@/utils/cart-display'
import { buildExtraFeeGoodsRow } from '@/utils/extra-fees'

function sumExtraFees(fees) {
  const list = Array.isArray(fees) ? fees : []
  return list.reduce((sum, row) => sum + (Number(row && row.amount) || 0), 0)
}

function formatMoneyCell(val) {
  if (val === undefined || val === null || val === '') return ''
  let text = String(val).trim()
  if (!text) return ''
  if (text.indexOf('¥') === 0 || text.indexOf('￥') === 0) text = text.slice(1).trim()
  const num = Number(text)
  if (!Number.isFinite(num)) return '¥ ' + text
  return '¥ ' + num.toFixed(2)
}

function buildGoodsRow(item, index, showSize, showAreas) {
  const row = [index + 1, cartDisplayName(item)]
  if (showSize) row.push(item.goods_valuation == 1 ? '-' : (item.size_text || ''))
  row.push(item.parameter_text || '')
  if (showAreas) row.push(item.goods_areas ?? '')
  row.push(
    item.goods_num ?? '',
    item.goods_unit || '',
    formatMoneyCell(item.singleton),
    formatMoneyCell(item.goods_original_price),
    item.remarks && item.remarks !== '' ? item.remarks : '无',
  )
  return row
}

/** 与页面 preview 文案完全一致的客户信息行 */
export function buildCustomerLines(detail) {
  return [
    {
      left: `客户名称：${detail.customer_id || ''}`,
      right: `报价编号：${detail.order_number || ''}`,
    },
    {
      left: `收货地址：${detail.receiving_address || ''}`,
      right: `报价日期：${detail.createTime || ''}`,
    },
    {
      left: `联系人：${detail.contacts || ''}    联系电话：${detail.contacts_phone || ''}`,
      right: `发票类型：${detail.invoice_text || ''}`,
    },
  ]
}

/** 与页面 preview 文案完全一致的页脚行 */
export function buildFooterLines(detail) {
  const remarks = detail.remarks && detail.remarks !== '' ? detail.remarks : '无'
  const extraSum = sumExtraFees(detail.extra_fees)
  const extraPart = extraSum > 0 ? `    附加费用：${moneyText(extraSum.toFixed(2))}` : ''
  return [
    {
      left: `小计：${moneyText(detail.total_primary || detail.total)}    优惠价格：${moneyText(detail.preferential || 0)}${extraPart}`,
      right: `制单人：${detail.user_name || ''}`,
    },
    {
      left: `支付状态：${detail.pay_status_text || ''}`,
      right: `运输方式：${detail.delivery_text || ''}`,
    },
    {
      full: `备注：${remarks}`,
    },
  ]
}

export function buildOfferExcelMeta(detail) {
  const { headers, showSize, showAreas } = buildHeaders(detail.category)
  const printTitle = `${detail.category_name || '报价单'}`
  const goodsRows = (detail.goodslist || []).map((item, index) => buildGoodsRow(item, index, showSize, showAreas))
  const extraFees = Array.isArray(detail.extra_fees) ? detail.extra_fees : []
  extraFees.forEach((fee, idx) => {
    goodsRows.push(buildExtraFeeGoodsRow(fee, goodsRows.length, showSize, showAreas))
  })

  return {
    printTitle,
    title: `${printTitle} Excel预览`,
    sheetName: detail.order_number || '报价单',
    headers,
    showSize,
    showAreas,
    goodsRows,
    customerLines: buildCustomerLines(detail),
    footerLines: buildFooterLines(detail),
    colWidths: buildColumnWidths(headers),
  }
}

function writeSplitRow(ws, row, colCount, merges, leftText, rightText, style) {
  const split = Math.max(1, Math.floor(colCount / 2))
  setCell(ws, row, 0, leftText, style)
  if (split > 1) pushMerge(merges, row, 0, row, split - 1)
  if (rightText) {
    setCell(ws, row, split, rightText, style)
    if (split < colCount - 1) pushMerge(merges, row, split, row, colCount - 1)
  } else if (split > 1) {
    pushMerge(merges, row, 0, row, colCount - 1)
  }
}

function writeFullRow(ws, row, colCount, merges, text, style) {
  setCell(ws, row, 0, text, style)
  if (colCount > 1) pushMerge(merges, row, 0, row, colCount - 1)
}

function buildStyledWorksheet(detail) {
  const meta = buildOfferExcelMeta(detail)
  const colCount = meta.headers.length
  const ws = {}
  const merges = []
  const rowHeights = []
  let row = 0

  setCell(ws, row, 0, meta.printTitle, titleStyle())
  pushMerge(merges, row, 0, row, colCount - 1)
  rowHeights[row] = { hpt: 52 }
  row += 1

  meta.customerLines.forEach((line) => {
    writeSplitRow(ws, row, colCount, merges, line.left, line.right, infoLineStyle())
    rowHeights[row] = { hpt: 30 }
    row += 1
  })

  row += 1

  writeFullRow(ws, row, colCount, merges, '感谢贵司询价，现报价如下：', subtitleStyle())
  rowHeights[row] = { hpt: 34 }
  row += 1

  meta.headers.forEach((header, col) => {
    setCell(ws, row, col, header, headerStyle())
  })
  rowHeights[row] = { hpt: 28 }
  row += 1

  meta.goodsRows.forEach((goodsRow, index) => {
    goodsRow.forEach((value, col) => {
      setCell(ws, row, col, value, cellStyle(index % 2 === 1))
    })
    rowHeights[row] = { hpt: 24 }
    row += 1
  })

  meta.footerLines.forEach((line) => {
    if (line.full) {
      writeFullRow(ws, row, colCount, merges, line.full, footerStyle())
    } else {
      writeSplitRow(ws, row, colCount, merges, line.left, line.right, footerStyle())
    }
    rowHeights[row] = { hpt: 30 }
    row += 1
  })

  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row - 1, c: colCount - 1 } })
  ws['!merges'] = merges
  ws['!cols'] = meta.colWidths
  ws['!rows'] = rowHeights

  return { ws, meta }
}

export function downloadOfferExcel(detail) {
  const { ws, meta } = buildStyledWorksheet(detail)
  const wb = XLSX.utils.book_new()
  const sheetName = String(meta.sheetName).slice(0, 31)
  XLSX.utils.book_append_sheet(wb, ws, sheetName)
  XLSX.writeFile(wb, `${meta.sheetName}.xlsx`)
}
